import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import type { IReason, IReasonLabels } from "../../../../services/api.types";

const MAX_LEN = 120;
const OPTIONAL_LANGS = ["ru", "az", "ge"] as const;

export interface ReasonFormValues {
  value: string;
  labels: IReasonLabels;
}

interface Props {
  open: boolean;
  // null = add mode, otherwise edit mode.
  reason: IReason | null;
  onClose: () => void;
  // Resolves on success; throws Error(message) on failure so the dialog can show it inline.
  onSubmit: (values: ReasonFormValues) => Promise<void>;
}

export default function ReasonFormDialog({ open, reason, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [valueTouched, setValueTouched] = useState(false);
  const [labels, setLabels] = useState<Record<string, string>>({ en: "", ru: "", az: "", ge: "" });
  const [enError, setEnError] = useState("");
  const [valueError, setValueError] = useState("");
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValue(reason ? reason.value : "");
    setValueTouched(false);
    setLabels({
      en: reason?.labels.en ?? "",
      ru: reason?.labels.ru ?? "",
      az: reason?.labels.az ?? "",
      ge: reason?.labels.ge ?? "",
    });
    setEnError("");
    setValueError("");
    setServerError("");
    setSaving(false);
  }, [open, reason]);

  const setLabel = (lang: string, v: string) => {
    setLabels((prev) => ({ ...prev, [lang]: v }));
    // Add mode: value tracks the English label until the admin edits it.
    if (lang === "en" && !reason && !valueTouched) setValue(v);
  };

  const handleSave = async () => {
    const en = labels.en.trim();
    const v = value.trim();
    let bad = false;
    setEnError("");
    setValueError("");
    setServerError("");
    if (!en) {
      setEnError(t("reasons.validation.enRequired"));
      bad = true;
    }
    if (!reason && !v) {
      setValueError(t("reasons.validation.valueRequired"));
      bad = true;
    }
    if (bad) return;

    const out: IReasonLabels = { en };
    OPTIONAL_LANGS.forEach((k) => {
      // "" is sent on edit so the backend unsets a cleared language.
      const s = labels[k].trim();
      if (s || reason) out[k] = s;
    });

    setSaving(true);
    try {
      await onSubmit({ value: v, labels: out });
    } catch (e: any) {
      setServerError(e?.message || t("serviceErrors.serverError"));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{reason ? t("reasons.editTitle") : t("reasons.addTitle")}</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 pt-2">
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <TextField
            label={t("reasons.fields.value")}
            value={value}
            disabled={!!reason}
            onChange={(e) => {
              setValue(e.target.value);
              setValueTouched(true);
            }}
            error={!!valueError}
            helperText={valueError || (reason ? t("reasons.valueReadOnly") : "")}
            inputProps={{ maxLength: MAX_LEN }}
            size="small"
            fullWidth
          />
          <TextField
            label={t("reasons.fields.labelEn")}
            value={labels.en}
            onChange={(e) => setLabel("en", e.target.value)}
            error={!!enError}
            helperText={enError}
            inputProps={{ maxLength: MAX_LEN }}
            required
            size="small"
            fullWidth
          />
          {OPTIONAL_LANGS.map((k) => (
            <TextField
              key={k}
              label={t(`reasons.fields.label_${k}`)}
              value={labels[k]}
              onChange={(e) => setLabel(k, e.target.value)}
              helperText={t("reasons.fallbackNote")}
              inputProps={{ maxLength: MAX_LEN }}
              size="small"
              fullWidth
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("reasons.cancel")}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {t("reasons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
